import type { Lifecycle, Reach, ResourceStatus } from "@u/resourceStatus";
import { FlaskConical, Globe, Link2, Lock, Sparkles, Star } from "lucide-react";
import type { ComponentType, ReactNode } from "react";
import { useTranslation } from "react-i18next";

/**
 * Estado consistente para las cards de producto (TourCard, VenueCard, …).
 * Consume la forma normalizada `ResourceStatus` (ver `@u/resourceStatus`) para
 * que los tres productos rendericen igual.
 *
 * Dos modos de render:
 * - overlay (default): cluster absoluto sobre el thumbnail (grid layout).
 * - `inline`: chips de texto en la fila de meta de la card (list layout),
 *   mismo look que los chips de tipo/categoría — evita el ruido de iconos
 *   flotando sobre el thumbnail chico.
 *
 * El slot `extra` permite a una card agregar un badge propio fuera del modelo
 * común (p.ej. el candado de password de tour) en el mismo cluster (solo overlay).
 */

const LIFECYCLE_DOT: Record<Lifecycle, string> = {
  draft: "bg-warning",
  live: "bg-success",
  paused: "bg-muted-foreground/40",
  pending: "bg-info",
};

const LIFECYCLE_KEY: Record<Lifecycle, string> = {
  draft: "draft",
  live: "status_live",
  paused: "status_paused",
  pending: "status_pending",
};

const REACH_KEY: Record<Reach, string> = {
  private: "private",
  unlisted: "unlisted",
  listed: "portfolio_listing_on",
};

const REACH_ICON: Record<Reach, typeof Globe> = {
  private: Lock,
  unlisted: Link2,
  listed: Globe,
};

const chipBase =
  "flex items-center gap-1 bg-background/90 backdrop-blur-sm border border-border/40 rounded-sm shadow-sm text-xs font-mono uppercase tracking-wide text-foreground";

const inlineChip =
  "inline-flex items-center gap-1 rounded-full border border-border/40 bg-muted/30 px-2 py-0.5 text-xs font-mono uppercase tracking-widest text-muted-foreground";

type Chip = {
  key: string;
  label: string;
  icon?: ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;
  iconClass?: string;
  dot?: string;
};

export default function StatusBadges({
  status,
  extra,
  compact = false,
  inline = false,
}: {
  status: ResourceStatus;
  extra?: ReactNode;
  /** List layout overlay: thumbnail chico → solo iconos/dot, sin labels. */
  compact?: boolean;
  /** Render como chips de texto en la fila de meta (no overlay). */
  inline?: boolean;
}) {
  const { t } = useTranslation();
  const { lifecycle, reach, flags } = status;

  const chips: Chip[] = [];
  // Lifecycle chip solo en estados excepcionales. `live` = la norma → sin chip.
  if (lifecycle !== "live") {
    chips.push({
      key: "lifecycle",
      label: t(LIFECYCLE_KEY[lifecycle]),
      dot: LIFECYCLE_DOT[lifecycle],
    });
  }
  if (reach) {
    chips.push({
      key: "reach",
      label: t(REACH_KEY[reach]),
      icon: REACH_ICON[reach],
    });
  }
  if (flags.featured) {
    chips.push({
      key: "featured",
      label: t("featured", "Featured"),
      icon: Star,
      iconClass: "fill-current",
    });
  }
  if (flags.demo) {
    chips.push({ key: "demo", label: t("demo_badge"), icon: FlaskConical });
  }
  if (flags.template) {
    chips.push({ key: "template", label: t("template"), icon: Sparkles });
  }

  if (inline) {
    if (chips.length === 0) return null;
    return (
      <>
        {chips.map((c) => {
          const Icon = c.icon;
          return (
            <span key={c.key} className={inlineChip}>
              {c.dot && <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />}
              {Icon && <Icon size={11} strokeWidth={1.5} className={c.iconClass} />}
              {c.label}
            </span>
          );
        })}
      </>
    );
  }

  const pad = compact ? "p-1" : "px-1.5 py-0.5";
  return (
    <div className="absolute bottom-1.5 left-1.5 flex flex-wrap gap-1 transform translate-y-0.5 group-hover:translate-y-0 opacity-80 group-hover:opacity-100 transition-all duration-300">
      {chips.map((c) => {
        const Icon = c.icon;
        return (
          <div
            key={c.key}
            className={`${chipBase} ${pad}`}
            title={compact ? c.label : undefined}
          >
            {c.dot && <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />}
            {Icon && (
              <Icon
                size={11}
                strokeWidth={1.5}
                className={c.iconClass}
                aria-label={c.label}
              />
            )}
            {!compact && <span>{c.label}</span>}
          </div>
        );
      })}
      {extra}
    </div>
  );
}
